import { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Phone, MessageCircle, Zap, MapPin, ShieldCheck, ArrowLeft } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ChatWidget } from "@/components/site/ChatWidget";
import { Reveal } from "@/components/site/Reveal";
import { CITTA, TELEFONO, TELEFONO_HREF, SERVIZI_SELECT } from "@/data/citta";

const IMG_CITTA =
  "https://images.unsplash.com/photo-1520440229-6469a149ac59?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function CityPage() {
  const { slug } = useParams();
  const citta = CITTA.find((c) => c.slug === slug);

  useEffect(() => {
    if (citta) {
      document.title = `Pronto Intervento ${citta.nome} H24 — SOS Riparazioni 24`;
      window.scrollTo(0, 0);
    }
  }, [citta]);

  if (!citta) return <Navigate to="/" replace />;

  const altre = CITTA.filter((c) => c.slug !== slug).slice(0, 6);

  return (
    <div className="min-h-screen bg-obsidian" data-testid={`citta-page-${slug}`}>
      <Header />
      <main>
        <section className="relative flex min-h-[70vh] items-end overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={IMG_CITTA}
              alt={`Pronto intervento a ${citta.nome}`}
              className="h-full w-full object-cover opacity-25"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-obsidian/60 via-obsidian/70 to-obsidian" />
          </div>
          <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-40 text-center md:px-8">
            <Reveal>
              <Link
                to="/"
                data-testid="citta-torna-home"
                className="mb-8 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-amber-400"
              >
                <ArrowLeft size={15} /> Torna alla home
              </Link>
              <p className="overline-label">
                Attivi H24 · {citta.zona}
              </p>
              <h1 className="mx-auto mt-4 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-white text-balance sm:text-5xl lg:text-6xl">
                Pronto intervento <span className="text-amber-400">{citta.nome}</span>
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
                Idraulico, elettricista, fabbro, spurgo e assistenza caldaie a {citta.nome} e
                dintorni: i nostri tecnici sono già in zona, giorno e notte. Chiamaci e un
                professionista certificato sarà subito da te, con preventivo chiaro prima
                dell'intervento.
              </p>
              <div className="mt-9 flex flex-wrap justify-center gap-4">
                <a
                  href={TELEFONO_HREF}
                  data-testid="citta-cta-chiama"
                  className="inline-flex items-center gap-2.5 rounded-full bg-amber-500 px-8 py-4 text-base font-bold text-obsidian transition-all hover:-translate-y-0.5 hover:bg-amber-400"
                >
                  <Phone size={18} strokeWidth={2.5} /> Chiama ora · {TELEFONO}
                </a>
                <button
                  type="button"
                  data-testid="citta-cta-chat"
                  onClick={() => window.dispatchEvent(new CustomEvent("apri-chat"))}
                  className="inline-flex items-center gap-2.5 rounded-full border border-slate-600 px-8 py-4 text-base font-semibold text-white transition-all hover:border-amber-400 hover:text-amber-400"
                >
                  <MessageCircle size={18} /> Chatta con noi
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="border-y border-slate-800 bg-abyss py-14">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-5 text-center sm:grid-cols-3 md:px-8">
            {[
              { icon: Zap, v: "Intervento rapido", l: `Tecnici già in zona a ${citta.nome}` },
              { icon: ShieldCheck, v: "H24/7", l: "Copertura totale, anche festivi" },
              { icon: MapPin, v: citta.zona, l: "Zona di intervento coperta" },
            ].map((s) => (
              <div key={s.l} data-testid={`citta-stat-${s.l.split(" ")[0].toLowerCase()}`} className="flex flex-col items-center gap-3">
                <span className="rounded-xl bg-amber-500/15 p-3 text-amber-400">
                  <s.icon size={22} />
                </span>
                <div>
                  <p className="font-display text-xl font-bold text-white">{s.v}</p>
                  <p className="mt-1 text-sm text-slate-400">{s.l}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-20 md:px-8">
          <Reveal className="text-center">
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Interventi disponibili a {citta.nome}
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {SERVIZI_SELECT.map((s) => (
                <div
                  key={s}
                  data-testid={`citta-servizio-${s.split(" ")[0].toLowerCase()}`}
                  className="rounded-xl border border-slate-800 bg-slatecard px-5 py-4 text-center text-sm font-medium text-slate-200 transition-colors hover:border-amber-500/50"
                >
                  {s} a {citta.nome}
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1} className="mt-16 text-center">
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400">
              Interveniamo anche a
            </h3>
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              {altre.map((c) => (
                <Link
                  key={c.slug}
                  to={`/citta/${c.slug}`}
                  data-testid={`citta-altra-${c.slug}`}
                  className="rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-300 transition-colors hover:border-amber-500 hover:text-amber-400"
                >
                  {c.nome}
                </Link>
              ))}
            </div>
          </Reveal>
        </section>
      </main>
      <Footer />
      <ChatWidget />
    </div>
  );
}
